import axios, { AxiosResponse } from 'axios';
import type { ErrorMessage } from '@api/assistant/assistantApi';
import { isConflict, isSuccess, validationFailed } from '@api/apiRuntime';
import { SILENT_API_ERROR_DENYLIST } from '@api/silentApiErrorDenylist';
import { createLogger } from '@utils/logger';
import { ErrorData, isPageContextEnabled, recordError, resolveError } from './pageContextStore';

/**
 * Feeds the page context's unresolved errors from api.tsx#executeRequest — the one place every call of
 * the generated client passes through, before a page's own onError or onValidationFailed sees it (and
 * localizes the messages away). The assistant's own client does not go through there.
 *
 * Nothing here may affect the request: every hook swallows its own failures.
 */

const log = createLogger('assistantPageContext');

/** Method + path, without the host or the query string (trcUUID and the rest live in `params` anyway). */
export const errorKey = (httpMethod: string, url: string): string => {
  let pathname: string;
  try {
    pathname = new URL(url, 'http://localhost').pathname;
  } catch {
    pathname = url.split('?')[0];
  }
  return `${httpMethod.toUpperCase()} ${pathname}`;
};

interface RawMessage {
  message?: string;
  locized?: boolean;
  params?: unknown[];
  scope?: string;
  path?: string;
}

/** The envelope's `messages`, keeping the translation key and its params rather than the localized text. */
export const toErrorMessages = (raw: unknown): ErrorMessage[] =>
  (Array.isArray(raw) ? (raw as RawMessage[]) : []).map(m => ({
    key: m?.locized ? (m.message ?? null) : null,
    text: m?.locized ? null : (m?.message ?? null),
    params: (m?.params ?? []).map(p => String(p)),
    scope: m?.scope ?? '',
    path: m?.path ?? '',
  }));

/** A 2xx response whose envelope says VALIDATION or CONFLICT. */
export const errorDataFromResponse = (methodName: string, httpMethod: string, response: AxiosResponse): ErrorData => ({
  method: methodName,
  http_method: httpMethod.toUpperCase(),
  status: response.status,
  error_type: response.data?.errorType ?? null,
  error_code: null,
  trc_uuid: response.config?.params?.trcUUID ?? null,
  messages: toErrorMessages(response.data?.messages),
});

/** A thrown axios error: an HTTP error status, or a network failure with no response at all. */
export const errorDataFromAxiosError = (methodName: string, httpMethod: string, error: any): ErrorData => ({
  method: methodName,
  http_method: httpMethod.toUpperCase(),
  status: error?.response?.status ?? null,
  error_type: error?.response?.data?.errorType ?? null,
  error_code: error?.code ?? null,
  trc_uuid: error?.config?.params?.trcUUID ?? null,
  messages: toErrorMessages(error?.response?.data?.messages),
});

const ignored = (methodName: string) => !isPageContextEnabled() || SILENT_API_ERROR_DENYLIST.has(methodName);

/**
 * A response arrived. VALIDATION / CONFLICT envelopes are failures; a genuine success clears an earlier
 * failure of the same call. ADDITIONAL_ACTION is neither and leaves things as they are. A 204 "entity
 * not found" has an empty body and so counts as a success here, as it does for isSuccess.
 */
export const recordApiResponse = (httpMethod: string, url: string, methodName: string, response: AxiosResponse, isBinary: boolean) => {
  try {
    if (ignored(methodName)) return;
    const key = errorKey(httpMethod, url);
    if (isBinary) {
      if (response.status >= 200 && response.status < 300) resolveError(key);
      return;
    }
    if (validationFailed(response) || isConflict(response)) recordError(key, errorDataFromResponse(methodName, httpMethod, response));
    else if (isSuccess(response)) resolveError(key);
  } catch (error) {
    log.debug('Recording an API response for the assistant failed', error);
  }
};

/**
 * The call threw. Only axios errors are failures of the call: the same catch also receives exceptions
 * thrown by a page's own onSuccess, which are not. Cancelled requests are not failures either.
 */
export const recordApiFailure = (httpMethod: string, url: string, methodName: string, error: unknown) => {
  try {
    if (ignored(methodName)) return;
    if (axios.isCancel(error) || (error as any)?.code === 'ERR_CANCELED' || !axios.isAxiosError(error)) return;
    recordError(errorKey(httpMethod, url), errorDataFromAxiosError(methodName, httpMethod, error));
  } catch (recordingError) {
    log.debug('Recording an API failure for the assistant failed', recordingError);
  }
};
